import {
  type ImportGuide,
  ImportGuideSchema,
  type ImportJobListItem,
  ImportJobListItemSchema,
  type ImportJobListQuery,
  type ImportJobListResponse,
  ImportJobListResponseSchema,
  type ImportModuleDescriptor,
  ImportModuleDescriptorSchema,
  type ImportRowItem,
  ImportRowItemSchema,
  type ImportRowListQuery,
  type ImportRowListResponse,
  ImportRowListResponseSchema,
  type ImportSourceFile,
  ImportSourceFileSchema,
  type ImportTemplate,
  ImportTemplateSchema,
} from '@utazzo/shared';
import { z } from 'zod';

import { apiClient } from '@/lib/api-client';

/**
 * Imports admin API client (ADR-0036). Mirrors the server contract in
 * apps/api/src/modules/imports/imports.controller.ts:
 * GET /imports/modules
 * GET /imports/modules/:moduleKey
 * POST /imports/:moduleKey/upload (base64 body)
 * GET /imports/jobs
 * GET /imports/jobs/:jobId
 * GET /imports/jobs/:jobId/rows
 * PATCH /imports/jobs/:jobId/rows/:rowIndex
 * PATCH /imports/jobs/:jobId/rows/:rowIndex/skip
 * POST /imports/jobs/:jobId/commit
 */

export const listModules = async (): Promise<ImportModuleDescriptor[]> => {
  const { data } = await apiClient.get<unknown>('/imports/modules');
  return z.object({ modules: z.array(ImportModuleDescriptorSchema) }).parse(data).modules;
};

/**
 * Upload an XLSX as base64. File is read on the client via FileReader,
 * encoded, and POSTed inside a JSON body. The size cap is enforced
 * server-side (10MB raw); we still warn the user before encoding so the
 * round-trip isn't wasted.
 */
export const uploadImport = async (moduleKey: string, file: File): Promise<{ jobId: string }> => {
  const fileBase64 = await fileToBase64(file);
  const { data } = await apiClient.post<unknown>(
    `/imports/${moduleKey}/upload`,
    {
      fileName: file.name,
      fileBase64,
    },
    // Upload + validate parses the whole sheet and persists every row, so it
    // legitimately runs longer than a normal request. Override the client's
    // default 30s so a large (up to 5000-row) sheet has room to finish
    // instead of aborting with "timeout of 30000ms exceeded".
    { timeout: 120_000 },
  );
  return z.object({ jobId: z.string().uuid() }).parse(data);
};

const fileToBase64 = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = String(reader.result ?? '');
      // FileReader returns "data:<mime>;base64,<payload>" - strip the prefix.
      const idx = result.indexOf(',');
      resolve(idx >= 0 ? result.slice(idx + 1) : result);
    };
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });

export const listJobs = async (query: ImportJobListQuery): Promise<ImportJobListResponse> => {
  const { data } = await apiClient.get<unknown>('/imports/jobs', { params: query });
  return ImportJobListResponseSchema.parse(data);
};

export const getJob = async (jobId: string): Promise<ImportJobListItem> => {
  const { data } = await apiClient.get<unknown>(`/imports/jobs/${jobId}`);
  return ImportJobListItemSchema.parse(data);
};

export const listRows = async (
  jobId: string,
  query: ImportRowListQuery,
): Promise<ImportRowListResponse> => {
  const { data } = await apiClient.get<unknown>(`/imports/jobs/${jobId}/rows`, {
    params: query,
  });
  return ImportRowListResponseSchema.parse(data);
};

/**
 * Patch a row's fix cells. `sheetKey` is "" for single-sheet importers
 * (Leads), or a sheet name like "Hotels" for multi-sheet importers. The
 * server has two endpoints (legacy + multi); the client picks based on
 * the sheetKey so consumers don't have to know.
 */
export const patchRow = async (
  jobId: string,
  sheetKey: string,
  rowIndex: number,
  fixedCells: Record<string, unknown>,
): Promise<ImportRowItem> => {
  const path = sheetKey
    ? `/imports/jobs/${jobId}/sheets/${encodeURIComponent(sheetKey)}/rows/${rowIndex}`
    : `/imports/jobs/${jobId}/rows/${rowIndex}`;
  const { data } = await apiClient.patch<unknown>(path, { fixedCells });
  return ImportRowItemSchema.parse(data);
};

export const skipRow = async (
  jobId: string,
  sheetKey: string,
  rowIndex: number,
  skip: boolean,
): Promise<ImportRowItem> => {
  const path = sheetKey
    ? `/imports/jobs/${jobId}/sheets/${encodeURIComponent(sheetKey)}/rows/${rowIndex}/skip`
    : `/imports/jobs/${jobId}/rows/${rowIndex}/skip`;
  const { data } = await apiClient.patch<unknown>(path, { skip });
  return ImportRowItemSchema.parse(data);
};

export const commitJob = async (jobId: string): Promise<ImportJobListItem> => {
  // When the commit queue is enabled the server returns almost immediately
  // (status COMMITTING) and the job poll drives the progress bar. But if the
  // queue is disabled the commit runs inline and can legitimately exceed the
  // client's default 30s for a large sheet - give it the same headroom as
  // upload so it doesn't abort with a timeout mid-commit.
  const { data } = await apiClient.post<unknown>(`/imports/jobs/${jobId}/commit`, undefined, {
    timeout: 120_000,
  });
  return ImportJobListItemSchema.parse(data);
};

/**
 * Retry the rows that failed to commit: the server resets COMMIT_FAILED rows
 * back to VALID and re-commits them in one call. Same long timeout as commit
 * because the inline path can re-run a large batch.
 */
export const retryFailedRows = async (jobId: string): Promise<ImportJobListItem> => {
  const { data } = await apiClient.post<unknown>(`/imports/jobs/${jobId}/retry-failed`, undefined, {
    timeout: 120_000,
  });
  return ImportJobListItemSchema.parse(data);
};

/** Re-run validation over an already-uploaded job (no re-upload). */
export const revalidateJob = async (jobId: string): Promise<ImportJobListItem> => {
  const { data } = await apiClient.post<unknown>(`/imports/jobs/${jobId}/revalidate`, undefined, {
    timeout: 120_000,
  });
  return ImportJobListItemSchema.parse(data);
};

/** Fetch a short-lived presigned URL to download the original XLSX. */
export const getSourceFile = async (jobId: string): Promise<ImportSourceFile> => {
  const { data } = await apiClient.get<unknown>(`/imports/jobs/${jobId}/source-file`);
  return ImportSourceFileSchema.parse(data);
};

/** Fetch a starter XLSX for the given importer. */
export const getSampleTemplate = async (moduleKey: string): Promise<ImportTemplate> => {
  const { data } = await apiClient.get<unknown>(`/imports/${moduleKey}/template`);
  return ImportTemplateSchema.parse(data);
};

/**
 * Trigger a browser download of the importer's sample template. Wraps
 * `getSampleTemplate` with the base64-decode + Blob URL ceremony.
 */
export const downloadSampleTemplate = async (moduleKey: string): Promise<void> => {
  const template = await getSampleTemplate(moduleKey);
  const bytes = Uint8Array.from(atob(template.contentBase64), (c) => c.charCodeAt(0));
  const blob = new Blob([bytes], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = template.filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  // Defer revoke so Safari has time to start the download.
  setTimeout(() => URL.revokeObjectURL(url), 1000);
};

/**
 * Fetch the per-entity operator guide (Markdown) for the given
 * importer. Source files live at
 * `docs/operator-guides/bulk-imports/<moduleKey>.md`.
 */
export const getOperatorGuide = async (moduleKey: string): Promise<ImportGuide> => {
  const { data } = await apiClient.get<unknown>(`/imports/${moduleKey}/guide`);
  return ImportGuideSchema.parse(data);
};

/**
 * Trigger a browser download of the importer's operator guide (.md).
 * Mirrors `downloadSampleTemplate` for the markdown payload.
 */
export const downloadOperatorGuide = async (moduleKey: string): Promise<void> => {
  const guide = await getOperatorGuide(moduleKey);
  const bytes = Uint8Array.from(atob(guide.contentBase64), (c) => c.charCodeAt(0));
  const blob = new Blob([bytes], { type: 'text/markdown' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = guide.filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
};
